import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  Zap, Flame, Download, Loader2, Users, Handshake, Clock, TrendingUp, Building2, Megaphone, Filter, RotateCcw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = { luz: '#eab308', gas: '#f97316', luz_gas: '#10b981' };
const PIE_COLORS = ['#eab308', '#f97316', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];

const pct = (n, total) => (total > 0 ? Math.round((n / total) * 1000) / 10 : 0);

const ProductionReportPage = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [partners, setPartners] = useState([]);
  const [comerciais, setComerciais] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);

  const [filters, setFilters] = useState({
    partner_id: '', comercial_id: '', date_from: '', date_to: '', campaign: '',
  });

  useEffect(() => {
    axios.get(`${API_URL}/leads/assignable-partners`).then(r => setPartners(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/leads/assignable-users`).then(r => setComerciais(r.data || [])).catch(() => {});
  }, []);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    return p.toString();
  }, [filters]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/dashboard/production-report?${queryString}`);
      setReport(res.data);
      if (!filters.campaign && !filters.partner_id && !filters.comercial_id) {
        setAllCampaigns((res.data?.by_campaign || []).map(c => c.campanha));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/dashboard/production-report/excel?${queryString}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_producao_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado');
    } catch (err) {
      toast.error('Erro ao exportar Excel');
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => setFilters({ partner_id: '', comercial_id: '', date_from: '', date_to: '', campaign: '' });

  const filteredComerciais = filters.partner_id
    ? comerciais.filter(c => c.partner_id === filters.partner_id)
    : comerciais;

  const sb = report?.service_breakdown || { luz: 0, gas: 0, luz_gas: 0, total: 0 };
  const pieData = [
    { name: 'Luz', value: sb.luz, key: 'luz' },
    { name: 'Gás', value: sb.gas, key: 'gas' },
    { name: 'Luz + Gás', value: sb.luz_gas, key: 'luz_gas' },
  ].filter(d => d.value > 0);
  const rt = report?.response_time || {};

  return (
    <Layout>
      <div className="space-y-6" data-testid="production-report-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-[#2D3E6F]" /> Relatórios de Produção
            </h1>
            <p className="text-sm text-slate-500">Análise por comercial, equipa, campanha, empresa e tipo de serviço · {report?.total_contracts ?? 0} contratos</p>
          </div>
          <Button onClick={handleExport} disabled={exporting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="export-excel-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Exportar Excel
          </Button>
        </div>

        {/* Filtros */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700">
              <Filter className="w-4 h-4" /> Filtros
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div>
                <Label className="text-xs">Parceiro</Label>
                <Select value={filters.partner_id || '__all__'} onValueChange={(v) => setFilters(f => ({ ...f, partner_id: v === '__all__' ? '' : v, comercial_id: '' }))}>
                  <SelectTrigger data-testid="filter-partner"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os parceiros</SelectItem>
                    {partners.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Comercial</Label>
                <Select value={filters.comercial_id || '__all__'} onValueChange={(v) => setFilters(f => ({ ...f, comercial_id: v === '__all__' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-comercial"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os comerciais</SelectItem>
                    {filteredComerciais.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Campanha</Label>
                <Select value={filters.campaign || '__all__'} onValueChange={(v) => setFilters(f => ({ ...f, campaign: v === '__all__' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-campaign"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas as campanhas</SelectItem>
                    {allCampaigns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">De</Label>
                <input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" data-testid="filter-date-from" />
              </div>
              <div>
                <Label className="text-xs">Até</Label>
                <input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))} className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" data-testid="filter-date-to" />
              </div>
              <div className="flex items-end">
                <Button variant="outline" onClick={resetFilters} className="w-full" data-testid="reset-filters-btn">
                  <RotateCcw className="w-4 h-4 mr-2" /> Limpar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#2D3E6F]" /></div>
        ) : (
          <>
            {/* Tipo de serviço + tempo de resposta */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-0 shadow-sm">
                <CardHeader><CardTitle className="text-base">Distribuição por Tipo de Serviço</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-100" data-testid="svc-luz">
                      <Zap className="w-5 h-5 text-yellow-500 mx-auto mb-1" />
                      <p className="text-xl font-bold text-yellow-600">{sb.luz}</p>
                      <p className="text-xs text-slate-500">Luz · {pct(sb.luz, sb.total)}%</p>
                    </div>
                    <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-100" data-testid="svc-gas">
                      <Flame className="w-5 h-5 text-orange-500 mx-auto mb-1" />
                      <p className="text-xl font-bold text-orange-600">{sb.gas}</p>
                      <p className="text-xs text-slate-500">Gás · {pct(sb.gas, sb.total)}%</p>
                    </div>
                    <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-100" data-testid="svc-dual">
                      <div className="flex items-center justify-center gap-0.5 mb-1"><Zap className="w-4 h-4 text-yellow-500" /><Flame className="w-4 h-4 text-orange-500" /></div>
                      <p className="text-xl font-bold text-emerald-600">{sb.luz_gas}</p>
                      <p className="text-xs text-slate-500">Luz+Gás · {pct(sb.luz_gas, sb.total)}%</p>
                    </div>
                  </div>
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={(e) => `${e.name} ${pct(e.value, sb.total)}%`}>
                          {pieData.map((e) => <Cell key={e.key} fill={COLORS[e.key]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-sm text-slate-400 text-center py-8">Sem dados de energia</p>}
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-blue-500" /> Tempo de Resposta às Leads</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-blue-600">{rt.avg_hours ?? '—'}<span className="text-base">h</span></p>
                      <p className="text-xs text-slate-500 mt-1">Média até 1º contacto</p>
                    </div>
                    <div className="p-4 bg-indigo-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-indigo-600">{rt.median_hours ?? '—'}<span className="text-base">h</span></p>
                      <p className="text-xs text-slate-500 mt-1">Mediana</p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-emerald-600">{rt.under_24h_pct ?? '—'}%</p>
                      <p className="text-xs text-slate-500 mt-1">Respondidas &lt; 24h</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-slate-700">{rt.count ?? 0}</p>
                      <p className="text-xs text-slate-500 mt-1">Leads analisadas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tendência mensal */}
            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#2D3E6F]" /> Tendência Mensal</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={report?.monthly_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" fontSize={12} />
                    <YAxis fontSize={12} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="total" name="Total" stroke="#2D3E6F" strokeWidth={2} />
                    <Line type="monotone" dataKey="luz" name="Luz" stroke={COLORS.luz} strokeWidth={2} />
                    <Line type="monotone" dataKey="gas" name="Gás" stroke={COLORS.gas} strokeWidth={2} />
                    <Line type="monotone" dataKey="luz_gas" name="Luz+Gás" stroke={COLORS.luz_gas} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Por comercial */}
            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-sky-500" /> Produção por Comercial</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-500 border-b">
                        <th className="py-2 pr-3">Comercial</th><th className="py-2 px-3">Parceiro</th>
                        <th className="py-2 px-3 text-right">Total</th><th className="py-2 px-3 text-right">Concluídos</th>
                        <th className="py-2 px-3 text-right">Luz</th><th className="py-2 px-3 text-right">Gás</th><th className="py-2 px-3 text-right">Luz+Gás</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(report?.by_comercial || []).map((r) => (
                        <tr key={r.user_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`comercial-row-${r.user_id}`}>
                          <td className="py-2 pr-3 font-medium text-slate-800">{r.name}</td>
                          <td className="py-2 px-3 text-slate-500">{r.partner_name}</td>
                          <td className="py-2 px-3 text-right font-bold">{r.total}</td>
                          <td className="py-2 px-3 text-right text-emerald-600">{r.concluido}</td>
                          <td className="py-2 px-3 text-right text-yellow-600">{r.luz}</td>
                          <td className="py-2 px-3 text-right text-orange-600">{r.gas}</td>
                          <td className="py-2 px-3 text-right text-emerald-600">{r.luz_gas}</td>
                        </tr>
                      ))}
                      {(report?.by_comercial || []).length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-400">Sem dados</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Por equipa + Top empresas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-0 shadow-sm">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Handshake className="w-4 h-4 text-orange-500" /> Produção por Equipa</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={Math.max(180, (report?.by_team?.length || 1) * 44)}>
                    <BarChart data={report?.by_team || []} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis type="number" fontSize={12} allowDecimals={false} />
                      <YAxis type="category" dataKey="team_name" width={110} fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="total" name="Contratos" fill="#2D3E6F" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4 text-violet-500" /> Empresas Mais Contratadas</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {(report?.by_company || []).map((r, i) => {
                      const max = report?.by_company?.[0]?.count || 1;
                      return (
                        <div key={r.company} className="flex items-center gap-2" data-testid={`company-row-${i}`}>
                          <span className="text-xs w-28 truncate capitalize text-slate-700">{r.company}</span>
                          <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                            <div className="h-full rounded-full flex items-center justify-end pr-2 text-[10px] text-white font-bold" style={{ width: `${pct(r.count, max)}%`, backgroundColor: PIE_COLORS[i % PIE_COLORS.length], minWidth: '24px' }}>{r.count}</div>
                          </div>
                        </div>
                      );
                    })}
                    {(report?.by_company || []).length === 0 && <p className="text-center py-6 text-slate-400 text-sm">Sem dados</p>}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Por campanha */}
            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Megaphone className="w-4 h-4 text-pink-500" /> Produção por Campanha</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-500 border-b">
                        <th className="py-2 pr-3">Campanha</th>
                        <th className="py-2 px-3 text-right">Total</th>
                        <th className="py-2 px-3 text-right">Luz</th><th className="py-2 px-3 text-right">Gás</th><th className="py-2 px-3 text-right">Luz+Gás</th>
                        <th className="py-2 px-3">Empresa + Contratada</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(report?.by_campaign || []).map((r) => (
                        <tr key={r.campanha} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`campaign-row`}>
                          <td className="py-2 pr-3 font-medium text-slate-800">{r.campanha}</td>
                          <td className="py-2 px-3 text-right font-bold">{r.total}</td>
                          <td className="py-2 px-3 text-right text-yellow-600">{r.luz}</td>
                          <td className="py-2 px-3 text-right text-orange-600">{r.gas}</td>
                          <td className="py-2 px-3 text-right text-emerald-600">{r.luz_gas}</td>
                          <td className="py-2 px-3 text-slate-600 capitalize">{r.top_company} <span className="text-xs text-slate-400">({r.top_company_count})</span></td>
                        </tr>
                      ))}
                      {(report?.by_campaign || []).length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-400">Sem dados</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default ProductionReportPage;
